import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import { pickLocale, telHref, mailHref, mapsUrl } from "@/lib/helpers";
import { track } from "@/api/analytics";
import ImageGallery from "@/components/ImageGallery";
import ContentBlocks from "@/components/ContentBlocks";
import * as Icons from "lucide-react";
import { Loader2 } from "lucide-react";

/** A single action row (Call / Directions / Website / Email).
 *  When `primary` is true it gets a subtle bronze accent to guide the guest
 *  towards the most useful action for this specific POI.
 */
function ActionRow({ icon: Icon, label, value, href, onClick, testid, primary = false }) {
  if (!value) return null;
  const cls = primary
    ? "flex items-center gap-3 rounded-xl bg-cream-50 border border-bronze-300/70 px-4 py-3 hover:bg-cream-100 active:scale-[0.99] transition"
    : "flex items-center gap-3 rounded-xl border border-ink-200/50 bg-white px-4 py-3 hover:bg-cream-100 active:scale-[0.99] transition";
  const iconBoxCls = primary
    ? "w-9 h-9 rounded-lg bg-bronze-100 flex items-center justify-center"
    : "w-9 h-9 rounded-lg bg-cream-100 flex items-center justify-center";
  const iconColor = primary ? "text-bronze-500" : "text-corten-400";
  return (
    <a
      href={href}
      onClick={onClick}
      target={href?.startsWith("http") ? "_blank" : undefined}
      rel="noreferrer"
      data-testid={testid}
      className={cls}
    >
      <div className={iconBoxCls}>
        <Icon strokeWidth={1.4} className={`w-4 h-4 ${iconColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-300">{label}</div>
        <div className="text-sm font-medium text-ink-900 truncate">{value}</div>
      </div>
      <Icons.ArrowUpRight strokeWidth={1.4} className="w-4 h-4 text-ink-300 shrink-0" />
    </a>
  );
}

export default function POIDetail() {
  const { id } = useParams();
  const { i18n, t } = useTranslation();
  const [poi, setPoi] = useState(null);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get(`/public/pois/${id}`),
      api.get(`/public/categories`),
    ])
      .then(([pRes, cRes]) => {
        if (cancelled) return;
        setPoi(pRes.data);
        const cat = (cRes.data || []).find((c) => c.id === pRes.data?.category_id);
        setCategory(cat || null);
        track("view_poi", { target_type: "poi", target_id: id });
      })
      .catch(() => {
        // Leaves poi=null → the "…" not-found state is rendered.
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  /** Choose which action row deserves the bronze accent. */
  const primaryAction = useMemo(() => {
    if (!poi) return null;
    if (poi.phone) return "phone";
    if (poi.address || (poi.lat && poi.lng)) return "map";
    if (poi.website) return "website";
    if (poi.email) return "email";
    return null;
  }, [poi]);

  if (loading) return <div className="flex items-center justify-center py-20 text-ink-300"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (!poi) return null;

  const desc = pickLocale(poi.description, i18n.language);
  const hours = pickLocale(poi.hours, i18n.language);
  const notes = pickLocale(poi.notes, i18n.language);
  const mapHref = mapsUrl({ lat: poi.lat, lng: poi.lng, address: poi.address });
  const FallbackIcon = category?.icon
    ? (Icons[category.icon] || Icons.MapPin)
    : (poi.icon && Icons[poi.icon]) || Icons.MapPin;

  return (
    <div className="px-5 pt-4 pb-8">
      {poi.images?.length ? (
        <div className="animate-fade-up">
          <ImageGallery images={poi.images} />
        </div>
      ) : (
        <div
          className="animate-fade-up flex justify-center pt-6"
          data-testid="poi-hero-fallback"
        >
          <div className="w-24 h-24 rounded-full bg-bronze-50 border border-bronze-200/60 flex items-center justify-center">
            <FallbackIcon strokeWidth={1.3} className="w-9 h-9 text-bronze-400" />
          </div>
        </div>
      )}

      <div className="text-center pt-6 animate-fade-up" style={{ animationDelay: "60ms" }}>
        {poi.featured && (
          <span
            data-testid="poi-featured-badge"
            className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-bronze-500 bg-bronze-50 rounded-full px-2.5 py-0.5 mb-2"
          >
            <Icons.Star strokeWidth={1.6} className="w-3 h-3 text-bronze-500" />
            {t("poi.featured")}
          </span>
        )}
        <h1 data-testid="poi-title" className="font-editorial italic text-[34px] leading-tight text-ink-900">
          {pickLocale(poi.title, i18n.language)}
        </h1>
        {pickLocale(poi.subtitle, i18n.language) && (
          <p className="mt-1 text-sm text-ink-500">{pickLocale(poi.subtitle, i18n.language)}</p>
        )}
        <div className="flex justify-center my-3"><span className="ornament" /></div>
      </div>

      {desc && (
        <p
          className="text-[15px] text-ink-700 leading-relaxed animate-fade-up max-w-[600px] mx-auto"
          style={{ animationDelay: "120ms" }}
        >
          {desc}
        </p>
      )}

      {Array.isArray(poi.tags) && poi.tags.length > 0 && (
        <div
          data-testid="poi-tags"
          className="flex flex-wrap justify-center gap-1.5 mt-4 animate-fade-up max-w-[600px] mx-auto"
          style={{ animationDelay: "150ms" }}
        >
          {poi.tags.map((tag) => (
            <span
              key={tag}
              data-testid={`poi-tag-${tag}`}
              className="inline-flex items-center text-[11px] font-semibold uppercase tracking-[0.14em] text-bronze-500 bg-bronze-50 border border-bronze-200/60 rounded-full px-2.5 py-1"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      <div
        className="flex flex-col gap-2 mt-5 animate-fade-up max-w-[600px] mx-auto"
        style={{ animationDelay: "180ms" }}
      >
        <ActionRow
          icon={Icons.Phone}
          label={t("poi.call")}
          value={poi.phone}
          href={telHref(poi.phone)}
          onClick={() => track("click_phone", { target_type: "poi", target_id: poi.id })}
          testid="poi-action-phone"
          primary={primaryAction === "phone"}
        />
        <ActionRow
          icon={Icons.MapPin}
          label={t("poi.directions")}
          value={poi.address || (poi.lat ? `${poi.lat}, ${poi.lng}` : null)}
          href={mapHref}
          onClick={() => track("click_maps", { target_type: "poi", target_id: poi.id })}
          testid="poi-action-map"
          primary={primaryAction === "map"}
        />
        <ActionRow
          icon={Icons.Globe}
          label={t("poi.website")}
          value={poi.website}
          href={poi.website}
          onClick={() => track("click_website", { target_type: "poi", target_id: poi.id })}
          testid="poi-action-website"
          primary={primaryAction === "website"}
        />
        <ActionRow
          icon={Icons.Mail}
          label={t("poi.email")}
          value={poi.email}
          href={mailHref(poi.email)}
          onClick={() => track("click_email", { target_type: "poi", target_id: poi.id })}
          testid="poi-action-email"
          primary={primaryAction === "email"}
        />
      </div>

      {hours && (
        <section
          className="mt-5 rounded-2xl bg-ivory-100 border border-ink-200/40 p-4 animate-fade-up max-w-[600px] mx-auto"
          style={{ animationDelay: "240ms" }}
        >
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-bronze-500 mb-1">{t("poi.hours")}</div>
          <p className="text-sm text-ink-700 whitespace-pre-line">{hours}</p>
        </section>
      )}
      {notes && (
        <section className="mt-3 rounded-2xl bg-ivory-100 border border-ink-200/40 p-4 animate-fade-up max-w-[600px] mx-auto">
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-bronze-500 mb-1">{t("poi.notes")}</div>
          <p className="text-sm text-ink-700 whitespace-pre-line">{notes}</p>
        </section>
      )}

      {poi.blocks?.length > 0 && (
        <section className="mt-6 animate-fade-up max-w-[600px] mx-auto">
          <ContentBlocks blocks={poi.blocks} />
        </section>
      )}
    </div>
  );
}
